import { EmptyObject } from "@lookiero/sty-psp-typescript";

type ActionIcon =
  | "next_best_action.icon.brands"
  | "next_best_action.icon.photos"
  | "next_best_action.icon.colors_and_patterns"
  | "next_best_action.icon.prices"
  | "next_best_action.icon.measurements";

enum NextBestActionType {
  HOST_DEFAULT = "HOST_DEFAULT",
  CARD = "CARD",
  CTA = "CTA",
  TEXT = "TEXT",
  HOST_WEIGHT_METRIC = "HOST_WEIGHT_METRIC",
  HOST_WEIGHT_IMPERIAL = "HOST_WEIGHT_IMPERIAL",
  HOST_TEXT = "HOST_TEXT",
  NUMBER_WITH_CURRENCY = "NUMBER_WITH_CURRENCY",
}

enum NextBestActionLayout {
  GRID = "GRID",
  VERTICAL_LIST = "VERTICAL_LIST",
  HORIZONTAL_LIST = "HORIZONTAL_LIST",
}

interface LayoutColumns {
  readonly S: number;
  readonly M: number;
  readonly L: number;
}

interface HostMetadata {
  readonly layout: NextBestActionLayout;
  readonly layoutColumns?: LayoutColumns;
}

interface CardMetadata {
  readonly slug: string;
  readonly ctaTranslationKey: string;
  readonly icon: ActionIcon;
  readonly descriptionTranslationKey: string;
}

type NextBestActionTypesWithChildren = NextBestActionType.HOST_DEFAULT | NextBestActionType.HOST_TEXT;

type NextBestActionTypesWithOptionalTranslationKey = NextBestActionType.HOST_DEFAULT;

type NextBestActionTypesWithMetadata = NextBestActionType.HOST_DEFAULT | NextBestActionType.CARD;

type NextBestActionTypesWithRequiredTranslationKey =
  | NextBestActionType.CARD
  | NextBestActionType.CTA
  | NextBestActionType.TEXT
  | NextBestActionType.HOST_TEXT
  | NextBestActionType.HOST_WEIGHT_METRIC
  | NextBestActionType.HOST_WEIGHT_IMPERIAL
  | NextBestActionType.NUMBER_WITH_CURRENCY;

type NextBestActionMetadata<T extends NextBestActionType> = T extends NextBestActionType.HOST_DEFAULT
  ? HostMetadata
  : T extends NextBestActionType.CARD
    ? CardMetadata
    : never;

type NextBestActionChildren<T extends NextBestActionType> = T extends NextBestActionTypesWithChildren
  ? { readonly children: NextBestActionDefinitionAction[] }
  : EmptyObject;

type NextBestActionTranslationKey<T extends NextBestActionType> =
  T extends NextBestActionTypesWithOptionalTranslationKey
    ? { readonly translationKey?: string }
    : T extends NextBestActionTypesWithRequiredTranslationKey
      ? { readonly translationKey: string }
      : EmptyObject;

type NextBestActionMetadataProperty<T extends NextBestActionType> = T extends NextBestActionTypesWithMetadata
  ? { readonly metadata: NextBestActionMetadata<T> }
  : EmptyObject;

type NextBestActionDefinitionAction<T extends NextBestActionType = NextBestActionType> = T extends infer K extends
  NextBestActionType
  ? { readonly id: string; readonly type: K } & NextBestActionTranslationKey<K> &
      NextBestActionChildren<K> &
      NextBestActionMetadataProperty<K>
  : never;

type NextBestActionDefinition = NextBestActionDefinitionAction[];

export type {
  NextBestActionDefinition as NextBestActionDefinitionProjection,
  NextBestActionDefinitionAction,
  ActionIcon as NextBestActionDefinitionActionIcon,
};

export { NextBestActionType, NextBestActionLayout };
